import React, { useEffect, useRef, useState } from 'react'
import propTypes from 'prop-types'
import { makeStyles } from '@material-ui/core/styles'
import { Box } from '@material-ui/core'
import InputBase from '@material-ui/core/InputBase'
import IconButton from '@material-ui/core/IconButton'
import SearchIcon from '@material-ui/icons/Search'

const useStyles = makeStyles((theme) => ({
	root: {
		display: 'flex'
	},
	inputHolder: {
		width: '100%',
		backgroundColor: 'white',
		borderRadius: '4px 25px 25px 4px',
		display: 'flex',
		border: `0.5px solid ${theme.palette.colors.purple}`
	},
	input: {
		marginLeft: theme.spacing(1),
		flex: 1
	},
	iconButton: {
		padding: 10,
		backgroundColor: theme.palette.colors.purple,
		color: theme.palette.common.white,
		'&:hover': {
			backgroundColor: theme.palette.colors.purple,
			opacity: 0.75,
			color: theme.palette.common.white
		}
	}
}))

const DEFAULT_DEBOUNCE_MS = 300

/**
 * Search box that fires `onDebouncedChange(value)` once after `debounceMs`
 * of input inactivity. Visually identical to `SearchToolbox` so the user
 * doesn't perceive a different control between pages; only the interaction
 * model is different (debounce vs explicit submit).
 */
const DebouncedSearchBox = (props) => {
	const classes = useStyles()
	const [value, setValue] = useState(props.initialValue || '')
	const timeoutRef = useRef(null)

	useEffect(() => {
		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current)
			}
		}
	}, [])

	// Sync internal state when `initialValue` changes from outside (e.g. when
	// a parent clears filters via URL state). Cancels any pending debounce so
	// the in-flight value doesn't overwrite the freshly-cleared one.
	useEffect(() => {
		setValue(props.initialValue || '')
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current)
			timeoutRef.current = null
		}
	}, [props.initialValue])

	const fireDebounced = (next) => {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current)
		}
		timeoutRef.current = setTimeout(() => {
			props.onDebouncedChange(next)
		}, props.debounceMs)
	}

	const onInputChange = (event) => {
		const next = event.target.value
		setValue(next)
		fireDebounced(next)
	}

	const onSubmit = (event) => {
		event.preventDefault()
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current)
		}
		props.onDebouncedChange(value)
	}

	return (
		<div className={classes.root}>
			<Box component='form' className={classes.inputHolder} onSubmit={onSubmit}>
				<InputBase
					className={classes.input}
					placeholder={props.placeholder}
					inputProps={{ 'aria-label': 'search' }}
					value={value}
					onChange={onInputChange}
				/>
				<IconButton type='submit' className={classes.iconButton} aria-label='search'>
					<SearchIcon />
				</IconButton>
			</Box>
		</div>
	)
}

DebouncedSearchBox.propTypes = {
	placeholder: propTypes.string,
	initialValue: propTypes.string,
	onDebouncedChange: propTypes.func.isRequired,
	debounceMs: propTypes.number
}

DebouncedSearchBox.defaultProps = {
	placeholder: 'Search...',
	initialValue: '',
	debounceMs: DEFAULT_DEBOUNCE_MS,
	onDebouncedChange: () => console.warn('No [onDebouncedChange] callback defined')
}

export default DebouncedSearchBox
